import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import * as DateFns from 'date-fns';
import moment from 'moment-timezone';

import { DateRangeSelector } from '../../Shared/Filters';
import PositionFilter from '../../Shared/Filters/PositionFilter';
import ZoneTypeFilter from '../../Shared/Filters/ZoneTypeFilter';
import ViewFilter from '../../Shared/Filters/ViewMetricsSelector/ViewMetricsSelector';
import {
  BALL,
  BY_PERIOD,
  CONDENSED,
  EMPTY_ARRAY,
  EXPANDED,
  FILTER_GLOBAL_KEYS,
  FILTER_KEYS,
  FUTURE_PRACTICE,
  GAME,
  LIVE_PRACTICE,
  PAST_GAME,
  PAST_PRACTICE,
  PERFORMANCE,
  PLAY_TYPE,
  POSITION_LACROSSE,
  PRACTICE,
  QUARTER,
  VIEW_LIVE,
  YEARS
} from '../../../config/constants';
import Export from './Export';
import SimpleMultiSelector from '../../Shared/Filters/SimpleMultiSelector';
import TimeSinceFilter from '../../Shared/Filters/TimeSinceFilter';
import TabSelector from '../../Shared/Filters/TabSelector';
import ShowDistanceGraphs from '../../Shared/Filters/ShowDistanceGraph';
import { getAllDateRanges, isCC, isNCAA, isOSULacrosse, YYYYMMDDToDate } from '../../../helpers';
import PositionLacrosseFilter from '../../Shared/Filters/PositionLacrosseFilter';
import SimpleSelector from '../../Shared/Filters/SimpleSelector';
import { useDataEventsState, useFiltersState, useUserState } from '../../../store/hooks';
import { useRouter } from '../../../helpers/customHooks';
import { PerformanceRoutes } from './types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export default ( {loadData, dataInProgress, setOpenDrawer}: { loadData: () => void, dataInProgress?:boolean, setOpenDrawer: Dispatch<SetStateAction<boolean>>, } ) => {
  
  const user = useUserState();
  const { [FILTER_GLOBAL_KEYS.WEEKS]: weeks } = useFiltersState('_', FILTER_GLOBAL_KEYS.WEEKS);
  const { dataEvent: data, meta } = useDataEventsState();
  const {
    params,
    eventType,
    pathname: path,
    forcedScopeForFilter: scopeForFilter
  } = useRouter<PerformanceRoutes>();
  const { tab, subTab, teamId, view } = params;
  const {
    [FILTER_KEYS.VIEW_LIVE]: viewData = VIEW_LIVE.types[0],
    [FILTER_KEYS.CUSTOM_LIVE_TABLE]: customData,
    [FILTER_KEYS.SHOW_DISTANCE_GRAPHS]: distanceGraph  } = useFiltersState(scopeForFilter, FILTER_KEYS.VIEW_LIVE, FILTER_KEYS.CUSTOM_LIVE_TABLE, FILTER_KEYS.SHOW_DISTANCE_GRAPHS);
  const [showFilter, setShowFilter] = useState(-1);
  const flagZoneType = path.split('/').find((val) => val === 'expanded');
  const close = () => setShowFilter(-1);
  const isCustom = viewData.value === VIEW_LIVE.types[1].value && (distanceGraph === null || distanceGraph === undefined)? true:false;
  const yearSeasonPractice = useMemo(() => [...YEARS].reverse().find(year => {
    const dateRanges = getAllDateRanges(year, weeks, isNCAA(user), true);
    const practiceDate = new Date(data.meta.startTime);
    return dateRanges.some((range) => (
      DateFns.isWithinInterval(practiceDate, {
        start: YYYYMMDDToDate(range.start),
        end: DateFns.endOfDay(YYYYMMDDToDate(range.end))
      })
    ));
  }) || new Date(data.meta.startTime).getFullYear(), [user, weeks]);
  
  const isMobile = window.matchMedia("only screen and (max-width: 900px)").matches;

  const onOpenAlertsClick = () => {
    setOpenDrawer(true);
  };
  const tooltip = (val?: string) => {
    if (val) {
      return (
        <Tooltip id="tool">
          <div style={{ color: '#0A6F9A' }}>{val}</div>
        </Tooltip>
      );
    }
    return <Tooltip hidden={true} id="no_tooltip" />;
  };

  const filters = () => {
    if (subTab === BY_PERIOD) {
      return (data.meta.type !== FUTURE_PRACTICE && isOSULacrosse(data.meta.homeTeam.teamId) ?
          <PositionLacrosseFilter
            type={POSITION_LACROSSE}
            show={showFilter === 1}
            close={close}
            open={() => setShowFilter(1)}
            forceScope={scopeForFilter}
            onApply={loadData}
          /> :
          data.meta.type !== FUTURE_PRACTICE && (
            <PositionFilter
              show={showFilter === 1}
              close={close}
              open={() => setShowFilter(1)}
              forceScope={scopeForFilter}
              onApply={loadData}
            />
          )
      );
    }
    const periods = [...((data && data.meta && data.meta.drills) || [])];
    if (tab === BALL) {
      periods.unshift({
        drillType: 'Ball Pre-Practice',
        endTs: 0,
        session_id: 0,
        startTs: 0,
        state: ''
      });
    }
    
   const disableTime = viewData.value !== VIEW_LIVE.types[1].value? false : customData?.every(data=>{
    if(viewData.value === VIEW_LIVE.types[1].value){
      if((data.name === '0.6' || data.name === '0.7')){
        return false;
      }
      return true;
    }else{
      return false;
    } 
    });

    const timeUpdated = meta && meta.type === LIVE_PRACTICE || meta.type === PAST_GAME || dataInProgress ? meta.dateUpdated : data && data.data && data.data.highlights.updateTimestamp;

    return (
      <>
        {data.meta.type !== FUTURE_PRACTICE && (
          <DateRangeSelector
            show={showFilter === 0}
            close={close}
            open={() => setShowFilter(0)}
            loadData={loadData}
            forceScope={scopeForFilter}
            initialDate={data.meta.type === PAST_PRACTICE || data.meta.type === PAST_GAME ? data.meta.startTime : undefined}
            practicesTab={eventType === GAME ? undefined : {
              yearSeason: yearSeasonPractice,
              teamId: data.meta && data.meta.homeTeam && data.meta.homeTeam.teamId
            }}
            isNcaa={data.meta.league === 'ncaa-fbs' || data.meta.league === 'ncaa-mlax'}
          />
        )}
        {eventType === GAME && data.meta.type !== FUTURE_PRACTICE && (
          <SimpleSelector
            show={showFilter === 9}
            close={close}
            open={() => setShowFilter(9)}
            type={PLAY_TYPE}
            forceScope={scopeForFilter}
            onApply={loadData}
            menuStyle={{
              width: '162px', maxWidth: '162px', minWidth: '162px',
              height: '184px', maxHeight: '184px', minHeight: '151px'
            }}
            listItemStyle={{
              height: '34px', maxHeight: '34px', minHeight: '34px'
            }}
          />
        )}
        {data.meta.type !== FUTURE_PRACTICE && isOSULacrosse(data.meta.homeTeam.teamId) ?
          <PositionLacrosseFilter
            type={POSITION_LACROSSE}
            show={showFilter === 1}
            close={close}
            open={() => setShowFilter(1)}
            onApply={loadData}
            forceScope={scopeForFilter}
          /> :
          data.meta.type !== FUTURE_PRACTICE && !isMobile && (
            <PositionFilter
              show={showFilter === 1}
              close={close}
              open={() => setShowFilter(1)}
              forceScope={scopeForFilter}
              onApply={loadData}
            />
          )}
        {eventType === GAME && data.meta.type !== FUTURE_PRACTICE && (
          <SimpleMultiSelector
            type={QUARTER}
            show={showFilter === 2}
            close={close}
            open={() => setShowFilter(2)}
            onApply={loadData}
            forceScope={scopeForFilter}
          />
        )}
        {eventType === PRACTICE && data.meta.type === PAST_PRACTICE && !isMobile && (
          <SimpleMultiSelector
            type={{
              name: 'Period',
              types: periods.map((period) => ({ value: period, name: period.drillType })),
              title: 'PERIOD',
              filterKey: FILTER_KEYS.PERIODS
            }}
            show={showFilter === 3}
            close={close}
            open={() => setShowFilter(3)}
            onApply={loadData}
            forceScope={scopeForFilter}
          />
        )}
        {data.meta.type !== FUTURE_PRACTICE && !flagZoneType && !isMobile && (
          <ZoneTypeFilter
            show={showFilter === 4}
            close={close}
            open={() => setShowFilter(4)}
            // loadLiveData={loadData}
            forceScope={scopeForFilter}
            customView={isCustom}
          />
        )}
        {(eventType === PRACTICE && data.meta.type === LIVE_PRACTICE) && !isMobile && (
          <TimeSinceFilter
            show={showFilter === 5}
            close={close}
            open={() => setShowFilter(5)}
            loadData={loadData}
            forceScope={scopeForFilter}
            customView={disableTime}
          />
        )}
        {data.meta.type !== FUTURE_PRACTICE && tab === PERFORMANCE && (subTab === EXPANDED || (subTab === BY_PERIOD && view === EXPANDED)) && !isMobile && (
          <ViewFilter
            show={showFilter === 6}
            close={close}
            open={() => setShowFilter(6)}
            forceScope={scopeForFilter}
            // menuStyle={{ width: '193px' }} // @@not-required
          />
        )}
        {data.meta.type !== FUTURE_PRACTICE && !isMobile &&  (
          <ShowDistanceGraphs
            show={showFilter === 8}
            close={close}
            open={() => setShowFilter(8)}
            forceScope={scopeForFilter}
          />
        )}
         {data.meta.type !== FUTURE_PRACTICE && tab === PERFORMANCE && (subTab === CONDENSED || (subTab === BY_PERIOD && view === CONDENSED)) && !isMobile && (
          <ViewFilter
            show={showFilter === 6}
            close={close}
            open={() => setShowFilter(6)}
            forceScope={scopeForFilter}
            liveTab={true}
          />
        )}
        {!isMobile && data.meta.type === LIVE_PRACTICE && tab === PERFORMANCE && !dataInProgress &&(
          <div>
            <OverlayTrigger
                 placement="bottom"
                 overlay={ tooltip('Open Alerts')}
                 key={1}>
                  <button style={{border: 'none', background: 'none', fontSize: '25px'}} className="mdi mdi-bell-ring-outline" onClick={() => onOpenAlertsClick()} />
            </OverlayTrigger>
          </div>
        )}
        <div className="p-left" style={{ right: '18px' }}>
          {data.meta.type !== FUTURE_PRACTICE && eventType !== GAME && (
            <label className="l-date">Data last
              updated: {timeUpdated ? DateFns.format(new Date(timeUpdated), 'MM/dd/yyyy hh:mm:ssaaaaa') + 'm ' : ''}{timeUpdated ? moment.tz(moment.tz.guess()).format('z') : ''}
            </label>
          )}
          <Export
            dataInProgress={dataInProgress}
            teamId={teamId ? teamId : data.meta.homeTeam.teamId}
            isLivePractice={data.meta.type === LIVE_PRACTICE? true : false}
            startTime={data.meta.startTime}
            isCustomLive={tab === PERFORMANCE && (subTab === CONDENSED || (view === CONDENSED)) && viewData.value === VIEW_LIVE.types[1].value}
          />
        </div>
      </>
    );
  };
  return (
    <div className="head-filters-01 games-filters" style={{ paddingLeft: '187px', height: '66px' }}>
      <TabSelector show={showFilter === 7} close={close} open={() => setShowFilter(7)} />
      {filters()}
    </div>
  );
};
